import { useQuery } from "@apollo/client";
import { compact, orderBy } from "lodash";
import moment from "moment";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  GetPreviousMeetingsQueryQuery,
  GetPreviousMeetingsQueryQueryVariables,
  MeetingHeaderFragmentFragment,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import useDebounce from "@components/use-debounce/use-debounce";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  getUrl,
  removeCurrentYear,
} from "@helpers/helpers";

import getPreviousMeetingsQuery from "../graphql/get-previous-meetings-query";
import { MeetingPageContext } from "./meeting-page-context";
import { buildMeetingSeriesDropdownOptions } from "./meeting-series-dropdown-options";
import { MeetingTOCDockContext } from "./meeting-toc-dock-context";

const previousMeetingsLimit = 15;

const PreviousMeetingsPopoverPanel = ({
  meeting,
  hideDateButton = false,
}: {
  meeting: MeetingHeaderFragmentFragment;
  hideDateButton?: boolean;
}) => {
  const meetingGroupId = meeting.meetingGroupId;
  const link = useLink();
  // When embedded (profile 1-on-1 tab) the arrows paginate within the embedder
  // instead of navigating to a new meeting page.
  const { onNavigateToMeeting, oneononeSubjectId, loadingMeetingId } =
    useContext(MeetingPageContext);
  // The floating TOC duplicates these controls when it has room to render
  // beside the reading column; hide the inline copy in that case.
  const { hasRoom: tocHasRoom } = useContext(MeetingTOCDockContext);
  // On the profile 1-on-1 tab the navigable series is the person's 1-on-1s as
  // the subject across every MeetingGroup (matches the list the tab shows);
  // everywhere else it's the current meeting's own MeetingGroup series.
  const scopeBySubject =
    oneononeSubjectId !== null && oneononeSubjectId !== undefined;

  // when hovered, we'll fetch all previous meetings
  const [isHovered, setIsHovered] = useState(false);

  // use ref, so it does not trigger a component render
  const loadingRef = useRef(false);

  // current meeting is the one displayed
  const [currentMeeting, setCurrentMeeting] = useState(meeting);
  const debouncedCurrentMeeting = useDebounce(currentMeeting, 1000);

  // dates
  const meetingDate = removeCurrentYear(
    moment(currentMeeting.startDatetime).format("ddd, ll"),
  );
  const endOfToday = moment().endOf("day").format();
  const maxFutureDate = moment().add(2, "month").endOf("day").format();

  // memoize variables to prevent extra api calls. Exactly one of subject /
  // meetingGroupId scopes the series; the other is null so its filter is skipped.
  const variables = useMemo(
    () => ({
      beforeDate: endOfToday,
      maxFutureDate,
      meetingGroupId: scopeBySubject ? null : meetingGroupId || -1,
      subject: scopeBySubject ? oneononeSubjectId : null,
      // Subject-scoped (profile tab) includes drafts/ignored so ad-hoc &
      // imported 1-on-1s are reachable, matching the list; the group series
      // keeps filtering them out (null = no filter).
      draft: scopeBySubject ? null : false,
      ignored: scopeBySubject ? null : false,
      limit: previousMeetingsLimit,
      after: null,
    }),
    [
      endOfToday,
      maxFutureDate,
      meetingGroupId,
      scopeBySubject,
      oneononeSubjectId,
    ],
  );
  const { data, loading, fetchMore } = useQuery<
    GetPreviousMeetingsQueryQuery,
    GetPreviousMeetingsQueryQueryVariables
  >(getPreviousMeetingsQuery, {
    variables,
  });

  // compute different meeting data
  const pastMeetings = data?.meetings ? assertEdgesNonNull(data.meetings) : [];
  const nextMeetings = data?.nextMeetings
    ? assertEdgesNonNull(data.nextMeetings)
    : [];
  const allMeetings = orderBy(
    compact([...pastMeetings, ...nextMeetings]),
    ["startDatetime"],
    ["asc"],
  );
  const currentMeetingIndex = allMeetings.findIndex(
    (m) => m.id === currentMeeting.id,
  );
  const hasMorePreviousMeetings = !!data?.meetings?.pageInfo?.hasNextPage;
  const endCursorMorePreviousMeetings = data?.meetings?.pageInfo?.endCursor;

  const meetingBeforeCurrentMeeting = allMeetings[currentMeetingIndex - 1];
  const meetingAfterCurrentMeeting = allMeetings[currentMeetingIndex + 1];

  const handleFetchMorePreviousMeetings = () => {
    // don't fetch more if it's already loading
    if (loadingRef.current) {
      return;
    }
    if (hasMorePreviousMeetings) {
      loadingRef.current = true;
      fetchMore({
        variables: {
          after: endCursorMorePreviousMeetings,
          merge: true,
        },
      })
        .then(() => {
          loadingRef.current = false;
        })
        .catch(() => {
          loadingRef.current = false;
        });
    }
  };

  // Build the shared "browse meetings in this series" dropdown options. When
  // we're paginating in place (onNavigateToMeeting in context) clicks insert
  // the meeting into the page; otherwise they redirect to its URL. The
  // setCurrentMeeting + debounced redirect below also still kicks in via the
  // chevrons so we keep that path for non-paginated cases.
  const dropdownOptions = buildMeetingSeriesDropdownOptions({
    pastMeetings,
    nextMeetings,
    activeMeetingId: currentMeeting?.id ?? null,
    hasMore: hasMorePreviousMeetings,
    loadingMore: loading,
    onLoadMore: handleFetchMorePreviousMeetings,
    onSelect: (m) => {
      // Only invoked in paginated contexts; non-paginated callers navigate via
      // the link `to` produced by `getHref` below.
      if (onNavigateToMeeting) {
        const isOlder = moment(m.startDatetime).isBefore(
          currentMeeting.startDatetime,
        );
        onNavigateToMeeting(m, isOlder ? "after" : "before");
      }
    },
    getHref: onNavigateToMeeting
      ? undefined
      : (m) => getUrl({ meetingGroupId: m.meetingGroupId, meetingId: m.id }),
  });

  // when user changes meeting cursors very quickly
  // we debounce, and redirect to the new meeting after a delay
  useEffect(() => {
    if (debouncedCurrentMeeting.id !== meeting.id) {
      link.redirect(
        getUrl({
          meetingGroupId: debouncedCurrentMeeting.meetingGroupId,
          meetingId: debouncedCurrentMeeting.id,
        }),
      );
    }
  }, [debouncedCurrentMeeting.id]);

  // - if user is looking at a past meeting that is not already loaded
  //   then we'll trigger fetching all previous meetings
  // - if user hovers on the component, we'll fetch all previous meetings
  //   trying to prevent fetching extra meetings
  const currentMeetingIsInPastAndNotInPastMeetings =
    moment().isAfter(currentMeeting.startDatetime) &&
    !pastMeetings.some((m) => m.id === currentMeeting.id);
  const userNeedsMorePastMeetings =
    isHovered || currentMeetingIsInPastAndNotInPastMeetings;
  const loadJustWhatWeNeed = currentMeetingIndex < previousMeetingsLimit * 2; // ensure to have at least one page more than what we need
  const hasMoreMeetingsToLoad =
    hasMorePreviousMeetings && endCursorMorePreviousMeetings;
  const shouldFetchMorePreviousMeetings =
    loadJustWhatWeNeed && userNeedsMorePastMeetings && hasMoreMeetingsToLoad;
  useEffect(() => {
    if (shouldFetchMorePreviousMeetings) {
      handleFetchMorePreviousMeetings();
    }
  }, [shouldFetchMorePreviousMeetings]);

  // update current meeting state when prop changes
  useEffect(() => {
    setCurrentMeeting(meeting);
  }, [meeting.id]);

  useEffect(() => {
    setIsHovered(false);
  }, [meeting.meetingGroupId]);

  return (
    <div className="flex items-center" onMouseEnter={() => setIsHovered(true)}>
      {!hideDateButton && (
        <Dropdown
          menuClassName="max-h-80 bg-white overflow-y-scroll"
          options={dropdownOptions}
        >
          <Button
            theme={buttonTheme.text}
            size={ButtonSize.medium}
            className="text-left @3xl/layout-header:min-w-[98px]"
          >
            {meetingDate}
          </Button>
        </Dropdown>
      )}
      {/* Calendar dropdown + up/down chevrons. In paginated contexts (meeting
          page list, profile 1-on-1 tab) these duplicate the floating TOC, so
          we hide them whenever the floating TOC has room to render beside the
          reading column (see useTocHasRoom). When the reading column gets too
          narrow the TOC hides and these inline controls take over. Extension /
          embedded sidebar contexts have no floating TOC at all (tocHasRoom is
          false), so the inline controls always show. */}
      <div
        className={classNames(
          "flex items-center",
          onNavigateToMeeting && tocHasRoom && "hidden",
        )}
      >
        <Dropdown
          menuClassName="max-h-80 bg-white overflow-y-scroll"
          options={dropdownOptions}
        >
          <Button
            theme={buttonTheme.iconGray}
            className="!text-gray-500"
            size={ButtonSize.medium}
            icon={getTopicflowIcon("calendar")}
            aria-label="View all meetings in this series"
          />
        </Dropdown>
        {/* Down arrow: navigate backwards in time (to an older / past
            meeting). Swaps to a spinner when its target is the meeting
            currently loading inline. */}
        {meetingBeforeCurrentMeeting &&
        meetingBeforeCurrentMeeting.id === loadingMeetingId ? (
          <div className="flex h-6 w-6 items-center justify-center">
            <Loading mini size="3" />
          </div>
        ) : (
          <Button
            disabled={!meetingBeforeCurrentMeeting}
            size={ButtonSize.medium}
            theme={buttonTheme.iconGray}
            className="!text-gray-500"
            icon={getTopicflowIcon("chevronDown")}
            aria-label="Previous meeting"
            to={
              meetingBeforeCurrentMeeting && !onNavigateToMeeting
                ? getUrl({
                    meetingGroupId: meetingBeforeCurrentMeeting?.meetingGroupId,
                    meetingId: meetingBeforeCurrentMeeting?.id,
                  })
                : undefined
            }
            onClick={(e) => {
              e.preventDefault();
              if (!meetingBeforeCurrentMeeting) return;
              if (onNavigateToMeeting) {
                onNavigateToMeeting(meetingBeforeCurrentMeeting, "after");
              } else {
                setCurrentMeeting(meetingBeforeCurrentMeeting);
              }
            }}
          />
        )}
        {/* Up arrow: navigate forward in time (to a more recent / future
            meeting). Same spinner-swap as the down chevron. */}
        {meetingAfterCurrentMeeting &&
        meetingAfterCurrentMeeting.id === loadingMeetingId ? (
          <div className="flex h-6 w-6 items-center justify-center">
            <Loading mini size="3" />
          </div>
        ) : (
          <Button
            disabled={!meetingAfterCurrentMeeting}
            size={ButtonSize.medium}
            theme={buttonTheme.iconGray}
            className="!text-gray-500"
            icon={getTopicflowIcon("chevronUp")}
            aria-label="Next meeting"
            to={
              meetingAfterCurrentMeeting && !onNavigateToMeeting
                ? getUrl({
                    meetingGroupId: meetingAfterCurrentMeeting?.meetingGroupId,
                    meetingId: meetingAfterCurrentMeeting?.id,
                  })
                : undefined
            }
            onClick={(e) => {
              e.preventDefault();
              if (!meetingAfterCurrentMeeting) return;
              if (onNavigateToMeeting) {
                onNavigateToMeeting(meetingAfterCurrentMeeting, "before");
              } else {
                setCurrentMeeting(meetingAfterCurrentMeeting);
              }
            }}
          />
        )}
      </div>
      {meeting.id !== currentMeeting.id &&
        currentMeeting.id !== debouncedCurrentMeeting.id && (
          <Loading mini size="4 ml-1" />
        )}
    </div>
  );
};

export default PreviousMeetingsPopoverPanel;
